import React from 'react'
import { ApolloProvider } from '@apollo/client'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import 'animate.css'
import dayjs from 'dayjs'
import calendar from 'dayjs/plugin/calendar'
import duration from 'dayjs/plugin/duration'
import isoWeek from 'dayjs/plugin/isoWeek'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import localeData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import updateLocale from 'dayjs/plugin/updateLocale'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import 'mapbox-gl/dist/mapbox-gl.css'
import { createRoot } from 'react-dom/client'
import 'react-toastify/dist/ReactToastify.css'
import { ThemeProvider } from 'styled-components'
import 'typeface-roboto'

import * as colors from 'src/styles/color-theme'
// Tailwind v4, the shadcn foundation behind the design toggle. PREFLIGHT IS
// OFF and source scanning is scoped — that file's header is the contract for
// why importing it changes nothing while the toggle is off.
import 'src/styles/tailwind.css'

import App from './App'
import client from './components/graphql/apollo'
import './setupTransifex'
import { ErrorBoundary } from './ui/ErrorBoundary'
import { initSentry, isSentryChunkLoading } from './utils/sentry'
import { reloadForStaleChunk } from './utils/staleChunkReload'

// Report front-end errors (gated on REACT_APP_SENTRY_DSN; no-op without one).
// Synchronous and cheap: attaches a window error buffer now and dynamic-imports
// the SDK once idle after first paint, flushing anything buffered (PRO-136).
initSentry()

// A deploy replaces content-hashed chunk filenames, so a tab left open across
// a deploy 404s on its next lazy-route import. Vite fires `vite:preloadError`
// on such a failed dynamic-import preload - refresh silently to the new build
// instead of crashing to the ErrorBoundary. The reload is loop-guarded so a
// persistently-broken build falls through to the normal error UI.
window.addEventListener('vite:preloadError', (event) => {
  // ...but not for the background Sentry chunk: telemetry is non-essential,
  // its loader already swallows a blocked/adblocked fetch, and reloading the
  // user's tab over it would be a far worse outcome than losing the reports.
  // Left un-prevented so the import still rejects into that loader's catch.
  if (isSentryChunkLoading()) return
  event.preventDefault()
  reloadForStaleChunk()
})

dayjs.extend(duration)
dayjs.extend(localeData)
dayjs.extend(calendar)
dayjs.extend(isoWeek)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)
dayjs.extend(updateLocale)
dayjs.extend(weekday)
dayjs.extend(weekOfYear)

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.mapstyles = process.env.REACT_APP_MAPBOX_STYLES

// React Query client — REST data layer for the products-and-pricing module.
// Coexists with Apollo (GraphQL); mirrors wastehero-frontend-v2 config.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

const root = createRoot(document.getElementById('main')!)
root.render(
  <QueryClientProvider client={queryClient}>
    <ApolloProvider client={client}>
      <ErrorBoundary>
        <ThemeProvider theme={colors}>
          <App />
        </ThemeProvider>
      </ErrorBoundary>
    </ApolloProvider>
  </QueryClientProvider>
)
